import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ALL_LEVELS = [1, 2, 3] as const;

interface ShareLevelDropdownProps {
  /** Current selection (1 = friends & family, 2 = friends of friends, 3 = anyone). */
  value: number;
  onChange: (level: number) => void;
  disabled?: boolean;
  /**
   * Locale cap from `Locale.maxShareConnectionLevel`. Options above this
   * are hidden from the open menu — the BE clamps via
   * `LEAST(listing, locale)` anyway, no reason to offer something the
   * chef can't achieve. The over-cap value (if it's currently selected)
   * is kept in the list so the active selection stays visible; once the
   * chef picks a lower option, the over-cap row disappears.
   * Default 3 = no narrowing.
   */
  maxLevel?: number;
}

/**
 * Compact dropdown variant of the 3-tier audience picker, used where the
 * stacked card rows of {@link ShareLevelPicker} take up too much space (the
 * per-dish override on the publish form). A custom in-app popover — not a
 * native <select> — so it matches the app's look. The trigger shows the
 * selected title with its description as a helper line beneath it; the open
 * menu reuses the title + description option rows. Strings live in the
 * `profile` namespace under `reach.*`.
 */
export const ShareLevelDropdown: React.FC<ShareLevelDropdownProps> = ({
  value,
  onChange,
  disabled,
  maxLevel = 3,
}) => {
  // Strict filter — only offer levels at or below the locale cap. An
  // over-cap saved value is NOT shown as an option.
  const visibleLevels = ALL_LEVELS.filter((l) => l <= maxLevel);
  // Visual "selected" / trigger title uses the clamped value so the
  // dropdown's closed-state label and the open-list highlight both
  // reflect the effective cap, not the over-cap saved value. Picking
  // here fires onChange with the picked level — store stays at the
  // original `value` until that happens. See user spec: "shown as
  // selected even though it's not unless they save".
  const activeLevel = Math.min(value, maxLevel);
  const { t } = useTranslation('profile');
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3 text-left transition-colors hover:bg-muted disabled:opacity-50"
      >
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">
            {t(`reach.level${activeLevel}.title`)}
          </p>
          <p className="text-xs text-muted-foreground">
            {t(`reach.level${activeLevel}.description`)}
          </p>
        </div>
        <ChevronDown
          size={18}
          className={`shrink-0 text-muted-foreground transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute z-10 mt-1 w-full overflow-hidden rounded-lg border border-border bg-card shadow-lg"
        >
          {visibleLevels.map((level) => {
            const active = activeLevel === level;
            return (
              <li key={level} role="option" aria-selected={active}>
                <button
                  type="button"
                  onClick={() => {
                    if (!active) onChange(level);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-muted ${
                    active ? 'bg-muted' : ''
                  }`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground">
                      {t(`reach.level${level}.title`)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t(`reach.level${level}.description`)}
                    </p>
                  </div>
                  {active && (
                    <Check className="h-4 w-4 shrink-0 text-brand-primary" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
